import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { publicApi, type SiteContentBlock, type SiteContentResponse } from '@/lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// Hybrid CMS / API content layer.
//
// The public website renders from three tiers, in priority order:
//   1. CMS/API content (public_content via /public/site-content) when present
//   2. Per-section API endpoints (features, workflow, benefits, ai-capabilities)
//   3. Static, localized fallback content (i18n locale files)
//
// If the backend is down or the CMS is empty, tiers 1 & 2 resolve to null and the
// static localized content renders — so no section ever disappears.
// ─────────────────────────────────────────────────────────────────────────────

interface SiteContentContextValue {
  content: SiteContentResponse | null;
  loading: boolean;
  /** 'api' once CMS content has been loaded, otherwise 'static'. */
  source: 'api' | 'static';
}

const SiteContentContext = createContext<SiteContentContextValue>({
  content: null,
  loading: false,
  source: 'static',
});

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<'api' | 'static'>('static');

  useEffect(() => {
    let active = true;
    publicApi
      .getSiteContent()
      .then((data) => {
        if (!active) return;
        if (data && (data.items || data.sections)) {
          setContent(data);
          setSource('api');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <SiteContentContext.Provider value={{ content, loading, source }}>
      {children}
    </SiteContentContext.Provider>
  );
}

/**
 * Returns the CMS override block for a marketing section, or null when the CMS
 * has no content for it (the caller then uses its static localized fallback).
 */
export function useSiteBlock(key: string): SiteContentBlock | null {
  const { content } = useContext(SiteContentContext);
  const block = content?.sections?.[key];
  return block && typeof block === 'object' ? block : null;
}

/**
 * Returns CMS-managed item lists (features, benefits, categories, workflow,
 * aiCapabilities) from the aggregated /public/site-content payload, or null.
 */
export function useSiteItems(key: string): Array<Record<string, unknown>> | null {
  const { content } = useContext(SiteContentContext);
  const items = content?.items?.[key];
  return Array.isArray(items) && items.length > 0 ? items : null;
}

/**
 * Fetches a public API list endpoint once and exposes the data (or null on
 * empty/error). The caller renders `data ?? staticFallback`, so the localized
 * fallback stays reactive to language changes while real API data — when present
 * — always wins. Fetch failures are swallowed: the section never disappears.
 */
export function useApiList<T>(
  fetcher: () => Promise<{ data?: T[] } | undefined>
): { data: T[] | null; loading: boolean } {
  const [data, setData] = useState<T[] | null>(null);
  const [loading, setLoading] = useState(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let active = true;
    fetcherRef
      .current()
      .then((res) => {
        if (active && res?.data && Array.isArray(res.data) && res.data.length > 0) {
          setData(res.data);
        }
      })
      .catch(() => {
        /* keep null → caller uses static fallback */
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { data, loading };
}
